import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Snackbar,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { fetchSalesProfitPage, fetchProfitByDatePage } from '../api';

const PAGE_SIZE = 10;

const ProfitReportPage = () => {
  const [profitsByDate, setProfitsByDate] = useState([]);
  const [salesWithProfits, setSalesWithProfits] = useState([]);
  const [message, setMessage] = useState('');
  const [profitByDatePage, setProfitByDatePage] = useState(1);
  const [salesProfitPage, setSalesProfitPage] = useState(1);
  const [profitByDateTotal, setProfitByDateTotal] = useState(0);
  const [salesProfitTotal, setSalesProfitTotal] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [tab, setTab] = useState(0);

  // Track if next/previous exists from API for both paginations
  const [salesProfitHasNext, setSalesProfitHasNext] = useState(false);
  const [salesProfitHasPrev, setSalesProfitHasPrev] = useState(false);
  const [profitByDateHasNext, setProfitByDateHasNext] = useState(false);
  const [profitByDateHasPrev, setProfitByDateHasPrev] = useState(false);

  useEffect(() => {
    if (tab === 0) fetchSalesProfit(salesProfitPage);
  }, [salesProfitPage, tab]);

  useEffect(() => {
    if (tab === 1) fetchProfitByDate(profitByDatePage);
  }, [profitByDatePage, tab]);

  const fetchSalesProfit = async (page) => {
    try {
      const data = await fetchSalesProfitPage(page, PAGE_SIZE);
      setSalesWithProfits(data.profit_by_sales.results);
      setSalesProfitTotal(data.profit_by_sales.count);
      setSalesProfitHasNext(!!data.profit_by_sales.next);
      setSalesProfitHasPrev(!!data.profit_by_sales.previous);
    } catch (error) {
      setMessage('Error fetching sales profit.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchProfitByDate = async (page) => {
    try {
      const data = await fetchProfitByDatePage(page, PAGE_SIZE);
      setProfitsByDate(data.profit_by_date.results);
      setProfitByDateTotal(data.profit_by_date.count);
      setProfitByDateHasNext(!!data.profit_by_date.next);
      setProfitByDateHasPrev(!!data.profit_by_date.previous);
    } catch (error) {
      setMessage('Error fetching profit by date.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 'lg', mx: 'auto' }}>
        <PageHeader title="Profit Reports" subtitle="View profit analytics and reports." />
        <Card variant="outlined">
          <CardContent>
            <Tabs value={tab} onChange={handleTabChange} aria-label="profit report tabs" sx={{ mb: 2 }}>
              <Tab label="Profits By Sale" />
              <Tab label="Profits By Date" />
            </Tabs>
            {tab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>Profits By Sale</Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Total Amount</TableCell>
                        <TableCell>Total Profit</TableCell>
                        <TableCell>Overall Discount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salesWithProfits.length > 0 ? (
                        salesWithProfits.map((sale) => (
                          <TableRow key={sale.id}>
                            <TableCell>
                              <Button
                                component={RouterLink}
                                to={`/sales/${sale.id}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                              >
                                {sale.id}
                              </Button>
                            </TableCell>
                            <TableCell>{sale.customer__name}</TableCell>
                            <TableCell>{sale.total_amount}</TableCell>
                            <TableCell>{sale.total_profit}</TableCell>
                            <TableCell>{sale.overall_discount}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5}>No sales data available</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => setSalesProfitPage((p) => Math.max(1, p - 1))}
                    disabled={!salesProfitHasPrev}
                    sx={{ mr: 2 }}
                  >
                    Previous
                  </Button>
                  <Pagination
                    count={Math.ceil(salesProfitTotal / PAGE_SIZE)}
                    page={salesProfitPage}
                    onChange={(_, value) => setSalesProfitPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setSalesProfitPage((p) => p + 1)}
                    disabled={!salesProfitHasNext}
                    sx={{ ml: 2 }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
            {tab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>Profits By Date</Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Total Profit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {profitsByDate.length > 0 ? (
                        profitsByDate.map((profit, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{profit.date_of_profit}</TableCell>
                            <TableCell>{profit.daily_profit}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2}>No profits data available</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => setProfitByDatePage((p) => Math.max(1, p - 1))}
                    disabled={!profitByDateHasPrev}
                    sx={{ mr: 2 }}
                  >
                    Previous
                  </Button>
                  <Pagination
                    count={Math.ceil(profitByDateTotal / PAGE_SIZE)}
                    page={profitByDatePage}
                    onChange={(_, value) => setProfitByDatePage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setProfitByDatePage((p) => p + 1)}
                    disabled={!profitByDateHasNext}
                    sx={{ ml: 2 }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
              <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                {message}
              </Alert>
            </Snackbar>
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default ProfitReportPage;
